// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import catalogStyles from '../../bank-upload/styles/institution-catalog.module.css';
import formStyles from '../../bank-upload/styles/bank-upload-form.module.css';
import { LabelWithInfoTooltip } from '@client/banks/components/shared/label-with-info-tooltip';
import { DateInput } from '@shared/ui-kit/components/date-input';
import { DeleteButton } from '@shared/ui-kit/components/delete-button';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { Input, Label } from '@shared/ui-kit/components/input';
import { OptionsSelector } from '@shared/ui-kit/components/options-selector';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { DECIMAL_SEPARATORS } from '@shared/ui-kit/utils/constants';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { Tooltip, Typography } from '@mui/material';
import { Fragment, type ReactNode } from 'react';
import { Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { useAccountForm } from './use-account-form';

export const AccountForm = defineComponent({
  displayName: 'AccountForm',
  useSetup: useAccountForm,
  View: ({
    formId,
    onSubmit,
    currencyOptions,
    control,
    handleSubmit,
    t,
    currencyPrefix,
    ownersFieldArray,
    accountTypeOptions,
    todayMaxDate,
    onAddOwner,
    isIbanReadOnly,
    isDrawerLayout,
    showIbanTooltip,
    ibanRequired,
    embed,
  }) => {
    const body: ReactNode = (
      <fieldset className="m-0 flex min-w-0 flex-col gap-7 border-0 p-0">
        <section className={formStyles['form-section']}>
          <Typography
            component="h3"
            variant="subtitle1"
            className={formStyles['section-title']}
          >
            <Translation
              i18nKey="aggregator.create-bank.accounts.section.details"
              ns="aggregator"
            />
          </Typography>

          <div
            className={classNames(
              formStyles['field-grid'],
              isDrawerLayout && formStyles['field-grid-drawer'],
            )}
          >
            <Controller
              control={control}
              name="iban"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  {showIbanTooltip ? (
                    <LabelWithInfoTooltip
                      htmlFor="bank-upload-account-iban"
                      required={ibanRequired}
                      error={!!fieldState.error}
                      tooltipI18nKey="aggregator.create-bank.accounts.iban-tooltip"
                    >
                      <Translation
                        i18nKey="aggregator.create-bank.accounts.iban"
                        ns="aggregator"
                      />
                    </LabelWithInfoTooltip>
                  ) : (
                    <Label
                      htmlFor="bank-upload-account-iban"
                      required={ibanRequired}
                      error={!!fieldState.error}
                    >
                      <Translation
                        i18nKey="aggregator.create-bank.accounts.iban"
                        ns="aggregator"
                      />
                    </Label>
                  )}
                  <Input
                    id="bank-upload-account-iban"
                    {...field}
                    value={field.value}
                    error={!!fieldState.error}
                    readOnly={isIbanReadOnly}
                    placeholder={t(
                      'aggregator.create-bank.accounts.iban-placeholder',
                      { ns: 'aggregator' },
                    )}
                    onChange={event => {
                      field.onChange(
                        event.target.value.replace(/[^A-Za-z0-9]/g, ''),
                      );
                    }}
                    data-testid="bank-upload-account-iban"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-account-name"
                    required
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.accounts.name"
                      ns="aggregator"
                    />
                  </Label>
                  <Input
                    id="bank-upload-account-name"
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    placeholder={t(
                      'aggregator.create-bank.accounts.name-placeholder',
                      { ns: 'aggregator' },
                    )}
                    data-testid="bank-upload-account-name"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="currency"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label required error={!!fieldState.error}>
                    <Translation
                      i18nKey="aggregator.create-bank.accounts.currency"
                      ns="aggregator"
                    />
                  </Label>
                  <OptionsSelector
                    optionsAvailable={[...currencyOptions]}
                    optionSelected={field.value ?? ''}
                    onChangeValueSelected={field.onChange}
                    defaultText={t('aggregator.create-bank.accounts.currency', {
                      ns: 'aggregator',
                    })}
                    selectType="white"
                    data-testid="bank-upload-account-currency"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="accountType"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label required error={!!fieldState.error}>
                    <Translation
                      i18nKey="aggregator.create-bank.accounts.type-label"
                      ns="aggregator"
                    />
                  </Label>
                  <OptionsSelector
                    optionsAvailable={[...accountTypeOptions]}
                    optionSelected={field.value ?? ''}
                    onChangeValueSelected={field.onChange}
                    defaultText={t('aggregator.create-bank.accounts.type-label', {
                      ns: 'aggregator',
                    })}
                    selectType="white"
                    data-testid="bank-upload-account-type"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="available"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-account-available"
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.accounts.available"
                      ns="aggregator"
                    />
                  </Label>
                  <NumericFormat
                    customInput={Input}
                    id="bank-upload-account-available"
                    value={field.value ?? ''}
                    onValueChange={({ floatValue }) =>
                      field.onChange(floatValue === undefined ? null : floatValue)
                    }
                    onBlur={field.onBlur}
                    name={field.name}
                    inputMode="decimal"
                    allowNegative
                    decimalScale={2}
                    thousandSeparator="."
                    decimalSeparator=","
                    allowedDecimalSeparators={DECIMAL_SEPARATORS}
                    prefix={currencyPrefix ? `${currencyPrefix} ` : undefined}
                    error={!!fieldState.error}
                    placeholder={t(
                      'aggregator.create-bank.accounts.available-placeholder',
                      { ns: 'aggregator' },
                    )}
                    data-testid="bank-upload-account-available"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="balanceDate"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-account-balance-date"
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.accounts.balance-date"
                      ns="aggregator"
                    />
                  </Label>
                  <DateInput
                    id="bank-upload-account-balance-date"
                    value={field.value ?? ''}
                    onChange={value => field.onChange(value || null)}
                    maxDate={todayMaxDate}
                    error={!!fieldState.error}
                    data-testid="bank-upload-account-balance-date"
                  />
                </div>
              )}
            />
          </div>
        </section>

        <section className={formStyles['form-section']}>
          <div className={formStyles['section-title-row']}>
            <Typography
              component="h3"
              variant="subtitle1"
              className={formStyles['section-title']}
            >
              <Translation
                i18nKey="aggregator.create-bank.accounts.section.owners"
                ns="aggregator"
              />
            </Typography>
            <Tooltip
              title={
                <Translation
                  i18nKey="aggregator.create-bank.accounts.add-owner"
                  ns="aggregator"
                />
              }
              placement="top"
            >
              <span className={formStyles['section-add-button']}>
                <IconButton
                  type="button"
                  icon="Plus"
                  variant="icon"
                  color="gray"
                  onClick={onAddOwner}
                  data-testid="bank-upload-account-add-owner"
                />
              </span>
            </Tooltip>
          </div>

          {ownersFieldArray.fields.length > 0 ? (
            <div className={formStyles['repeatable-list']}>
              {ownersFieldArray.fields.map((field, index) => (
                <Fragment key={field.id}>
                  <div className={formStyles['repeatable-row']}>
                    <Controller
                      control={control}
                      name={`owners.${index}.name`}
                      render={({ field: ownerField, fieldState }) => (
                        <div
                          className={classNames(
                            catalogStyles['field-stack'],
                            formStyles['owner-field'],
                          )}
                        >
                          <Label
                            htmlFor={`bank-upload-account-owner-${index}`}
                            error={!!fieldState.error}
                          >
                            <Translation
                              i18nKey="aggregator.create-bank.accounts.owner-name"
                              ns="aggregator"
                              values={{ index: index + 1 }}
                            />
                          </Label>
                          <Input
                            id={`bank-upload-account-owner-${index}`}
                            {...ownerField}
                            value={ownerField.value ?? ''}
                            error={!!fieldState.error}
                            placeholder={t(
                              'aggregator.create-bank.accounts.owner-placeholder',
                              { ns: 'aggregator' },
                            )}
                            data-testid={`bank-upload-account-owner-${index}`}
                          />
                        </div>
                      )}
                    />

                    <span className={formStyles['remove-row-button']}>
                      <DeleteButton
                        onClick={() => ownersFieldArray.remove(index)}
                        data-testid={`bank-upload-account-owner-remove-${index}`}
                        color="gray"
                      />
                    </span>
                  </div>
                </Fragment>
              ))}
            </div>
          ) : (
            <div
              className={formStyles['section-empty-hint']}
              data-testid="bank-upload-account-owners-empty"
            >
              <Typography
                variant="body2"
                className={formStyles['section-empty-message']}
              >
                <Translation
                  i18nKey="aggregator.create-bank.accounts.owners-empty-hint"
                  ns="aggregator"
                />
              </Typography>
            </div>
          )}
        </section>
      </fieldset>
    );

    if (embed) {
      return <div data-testid="bank-upload-account-form-embed">{body}</div>;
    }

    return (
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        {body}
      </form>
    );
  },
});
